import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import React from 'react';
import { apiLambda } from '../services/apiLambda';
import _ from 'lodash';
import { IDebts, IMandatoryDebt } from '../hooks/useDebts';

import { api } from '../services/api';
import { IGetBatchConsultStatusResponse } from '../hooks/types/BatchConsults';
import { getBatchDebtsByIdentifications } from '../hooks/useDebtsUserVehicle';
import {
  IDebtTotalPrice,
  isCreateBatchInstallmentsResponse,
} from '../hooks/types/CreateBatchInstallments';
import { createBatchInstallments } from '../hooks/usePayments';
import {
  areAllConsultDatesToday,
  filterBySelectedDebts,
  haveSameBatchId,
  sumDebts,
  sumDebtsByTypeAndPlate,
} from '../utils/debtsProcessing';

export type DebtType = 'ipva' | 'dpvat' | 'multas' | 'outros' | 'licenciamento';

const CUPOM_SPAN_REGEX = /(<span hidden>)(.*)(<\/span>)/;

export interface IDebtsByUf {
  uf: string;
  data: {
    placa: string;
    dpvat: number;
    ipva: number;
    licenciamento: number;
    multas: number;
    outros: number;
    total: number;
  }[];
}

export interface IConsult {
  nome_consulta: string;
  campos: {
    descricao: string;
    conteudo: string;
  }[];
  debitos: {
    codigo: number;
    emissor: string;
    descricao: string;
    descricaoAdicional?: string;
    vencimento: Date;
    validade: Date;
    valor: number;
    grupo: number;
  }[];
  pagarTudo: boolean;
  mensagemEntrada: string;
  mensagemSaida: string;
  codigo: number;
}

export interface IConsultDebt {
  codigo: number;
  descricao: string;
  descricaoAdicional: string;
  emissor: string;
  expired: boolean;
  grupo: number;
  validade: string;
  valor: number;
  vencimento: string;
  disable?: boolean;
  ano?: number;
  unica?: boolean;
  quantidadeAno?: number;
  onCheck: {
    select: number[];
    unSelect: number[];
  };
  onUnCheck: {
    select: number[];
    unSelect: number[];
  };
}

interface BatchPaymentContextData {
  handleSetCupom(cupom: string): void;
  cupomStatus: boolean;
  cupom: string;
  entryMessage: string;
  exitMessage: string;
  installmentsLoading: boolean;
  availableCoupon: string;
  selectedDebts: IDebts[];
  allDebtsSelected: IAllDebts[];
  setSelectedDebts: (item: IDebts[]) => void;
  handleSelectInstallment(installment: InstallmentInfo): void;
  handleSelectPix(): void;
  installments: InstallmentInfo[];
  pix: InstallmentInfo | undefined;
  batchCode: number | undefined;
  debtsForTable: IDebtsByUf[];
  handleGetConsult: () => void;
  validateBatch: () => Promise<boolean>;
  handleRefetchDebts: (
    consultsPackageStatus: IGetBatchConsultStatusResponse | undefined,
  ) => void;
  isPix: boolean;
  ipvaTotal: number;
  licenciamentoTotal: number;
  dpvatTotal: number;
  multasTotal: number;
  outrosTotal: number;
  debtsTotal: number;
  // consultCode: number | undefined;
  handleConsultClean: (keepDebts?: boolean) => void;
  // consultLoading: boolean;
  // consult: IConsult | undefined;
}

export interface IAllDebts {
  debtsCode: number;
  value: number;
  type: string;
  plate: string;
  uf: string;
  consultName: string;
  expirationDate: string;
  dueDate: string;
  description: string;
  additionalDescription: string;
  consultNumber: string; 
}

export interface InstallmentInfo {
  month: number;
  installmentPrice: number;
  totalPrice: number;
  checked: boolean;
  discount: number;
  cet: number;
}

interface IResponseInstallments {
  tipo: string;
  parcelas: number;
  cet?: number;
  valor: number;
  total: number;
  desconto?: number;
}

const BatchPaymentContext = createContext<BatchPaymentContextData>(
  {} as BatchPaymentContextData,
);

export const BatchPaymentProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [ipvaTotal, setIpvaTotal] = useState(0);
  const [licenciamentoTotal, setLicenciamentoTotal] = useState(0);
  const [dpvatTotal, setDpvatTotal] = useState(0);
  const [multasTotal, setMultasTotal] = useState(0);
  const [outrosTotal, setOutrosTotal] = useState(0);
  const [debtsTotal, setDebtsTotal] = useState(0);

  const [cupom, setCupom] = useState('');
  const [cupomStatus, setCupomStatus] = useState(false);
  const [availableCoupon, setAvaliableCoupon] = useState('');

  // const [ready, setReady] = useState<boolean>(false);

  // States Related to `PaymentMethods` component
  const [pix, setPix] = useState<InstallmentInfo | undefined>(undefined);
  const [isPix, setIsPix] = useState<boolean>(false);
  const [installments, setInstallments] = useState<InstallmentInfo[]>([]);

  const [entryMessage, setEntryMessage] = useState('');
  const [exitMessage, setExitMessage] = useState('');
  const [installmentsLoading, setInstallmentsLoading] = useState<boolean>(true);
  // const [consultLoading, setConsultLoading] = useState(false);
  // const [consultCode, setConsultCode] = useState<number | undefined>();
  const [batchCode, setBatchCode] = useState<number | undefined>();
  // const [consult, setConsult] = useState<IConsult | undefined>();

  const [selectedDebts, setSelectedDebts] = useState<IDebts[]>([]);
  const [debtsForTable, setDebtsForTable] = useState<IDebtsByUf[]>([]);
  const [allDebtsSelected, setAllDebtsSelected] = useState<IAllDebts[]>([]);

  function handleResponseInstallmentsToInstallments(
    installments: IResponseInstallments[],
  ) {
    console.log(
      'handleResponseInstallmentsToInstallments ~ Starting handleInstallmentsResponse...',
    );
    let checkedPix = false;
    let parcela = 12;

    const pix = JSON.parse(localStorage.getItem('@Zignet:pix')!);

    if (pix) {
      checkedPix = pix?.checked;
      setIsPix(checkedPix);
    }

    const parcelas = JSON.parse(localStorage.getItem('@Zignet:parcelas')!);

    if (parcelas) {
      parcela = parcelas.find((p: any) => p?.checked)?.month;
    }

    const newInstallments: InstallmentInfo[] = installments
      .filter((i) => i?.tipo === 'C')
      .filter((i, n) => i?.parcelas <= 12)
      .map((i) => ({
        month: i.parcelas,
        installmentPrice: i.valor,
        totalPrice: i.total,
        discount: i.desconto ?? 0,
        checked: checkedPix ? false : i.parcelas === parcela ? true : false,
        cet: i.cet ?? 0,
      }))
      .sort((a, b) => {
        return a.month < b.month ? 1 : -1;
      });

    const pixDefaultChecked = !(
      installments
        .filter((i) => i?.tipo === 'C')
        .filter((i, n) => i?.parcelas <= 12).length > 0
    );

    const pixInstallments: InstallmentInfo[] = installments
      ?.filter((i) => i.tipo === 'P')
      ?.map((i) => ({
        month: i?.parcelas,
        installmentPrice: i?.valor,
        totalPrice: i?.total,
        discount: i?.desconto ?? 0,
        checked: checkedPix || pixDefaultChecked ? true : false,
        cet: i?.cet ?? 0,
      }));

    setInstallments(newInstallments);
    setInstallmentsLoading(false);
    localStorage.setItem('@Zignet:parcelas', JSON.stringify(newInstallments));

    if (pixInstallments.length > 0) {
      setPix(pixInstallments?.[0] ?? undefined);
      localStorage.setItem('@Zignet:pix', JSON.stringify(pixInstallments?.[0]));
    }
  }

  function sumAllRowsColumns(data: IDebtsByUf[]) {
    const sumOfDebtsByUf = data.map((debts) => ({
      uf: debts.uf,
      ipva: sumDebts(debts?.data, 'ipva'),
      licenciamento: sumDebts(debts?.data, 'licenciamento'),
      multas: sumDebts(debts?.data, 'multas'),
      dpvat: sumDebts(debts?.data, 'dpvat'),
      outros: sumDebts(debts?.data, 'outros'),
      total: sumDebts(debts?.data, 'total'),
    }));

    setIpvaTotal(sumDebts(sumOfDebtsByUf, 'ipva'));
    setDpvatTotal(sumDebts(sumOfDebtsByUf, 'dpvat'));
    setMultasTotal(sumDebts(sumOfDebtsByUf, 'multas'));
    setLicenciamentoTotal(sumDebts(sumOfDebtsByUf, 'licenciamento'));
    setOutrosTotal(sumDebts(sumOfDebtsByUf, 'outros'));
    setDebtsTotal(sumDebts(sumOfDebtsByUf, 'total'));
  }

  useEffect(() => {
    const allDebts: IAllDebts[] = [];
    selectedDebts.forEach((debt) => {
      if (debt.onCheck && debt.onCheck.select) {
        debt.onCheck.select.forEach((mandatoryDebt) => {
          if (
            !allDebts
              .map((debt) => debt.debtsCode)
              .includes(mandatoryDebt.debtsCode)
          ) {
            allDebts.push({
              debtsCode: mandatoryDebt.debtsCode,
              value: mandatoryDebt.price,
              type: mandatoryDebt.type,
              plate: debt.vehiclePlate,
              uf: debt.vehicleUF,
              additionalDescription: debt.additionalDescription ?? '',
              consultName: debt.consultName,
              expirationDate: debt.expirationDate,
              dueDate: debt.dueDate,
              description: debt.description,
              consultNumber: mandatoryDebt.consultNumber,
            });
          }
        });
      }

      if (!allDebts.map((debt) => debt.debtsCode).includes(debt.debtsCode)) {
        allDebts.push({
          debtsCode: debt.debtsCode,
          value: debt.price,
          type: debt.type,
          plate: debt.vehiclePlate,
          uf: debt.vehicleUF,
          additionalDescription: debt.additionalDescription ?? '',
          consultName: debt.consultName,
          expirationDate: debt.expirationDate,
          dueDate: debt.dueDate,
          description: debt.description,
          consultNumber: debt.consultNumber,
        });
      }
    });

    setAllDebtsSelected(allDebts);

    const allUfs: string[] = [];
    selectedDebts.forEach((debt) => {
      if (!allUfs.includes(debt.vehicleUF)) {
        allUfs.push(debt.vehicleUF);
      }
    });

    const allDebtsByUf = allUfs.map((uf) => {
      const debtsByUf = allDebts.filter((debt) => uf === debt.uf);

      const allPlates: string[] = [];
      debtsByUf.forEach((debt) => {
        if (!allPlates.includes(debt.plate)) {
          allPlates.push(debt.plate);
        }
      });

      const allTypes: string[] = [];
      debtsByUf.forEach((debt) => {
        if (!allTypes.includes(debt.type)) {
          allTypes.push(debt.type);
        }
      });

      const data = allPlates.map((plate) => {
        const dpvat = allTypes.includes('dpvat')
          ? sumDebtsByTypeAndPlate('dpvat', plate, debtsByUf)
          : 0;
        const ipva = allTypes.includes('ipva')
          ? sumDebtsByTypeAndPlate('ipva', plate, debtsByUf)
          : 0;
        const licenciamento = allTypes.includes('licenciamento')
          ? sumDebtsByTypeAndPlate('licenciamento', plate, debtsByUf)
          : 0;
        const outros = allTypes.includes('outros')
          ? sumDebtsByTypeAndPlate('outros', plate, debtsByUf)
          : 0;
        const multas = allTypes.includes('multas')
          ? sumDebtsByTypeAndPlate('multas', plate, debtsByUf)
          : 0;

        const total = dpvat + ipva + licenciamento + outros + multas;

        return {
          placa: plate,
          dpvat,
          ipva,
          licenciamento,
          multas,
          outros,
          total,
        };
      });

      return {
        uf,
        data,
      };
    });

    sumAllRowsColumns(allDebtsByUf);
    setDebtsForTable(allDebtsByUf);
  }, [selectedDebts]);

  useEffect(() => {
    const cupomStatusOnStorage = localStorage.getItem('@Zignet:cupomStatus');
    const cupomOnStorage = localStorage.getItem('@Zignet:cupom');
    // const consultCodeOnStorage = localStorage.getItem('@Zignet:consultCode');
    const selectedDebtsOnStorage = localStorage.getItem(
      '@Zignet:selectedDebts',
    );
    const installmentsOnStorage = localStorage.getItem('@Zignet:parcelas');
    const pixOnStorage = localStorage.getItem('@Zignet:pix');

    const cupomStatusFromStorage: boolean =
      cupomStatusOnStorage && cupomStatusOnStorage !== 'undefined'
        ? JSON.parse(cupomStatusOnStorage)
        : false;

    // const consultCodeFromStorage: number | undefined =
    //   consultCodeOnStorage && consultCodeOnStorage !== 'undefined'
    //     ? JSON.parse(consultCodeOnStorage)
    //     : undefined;

    const cupomFromStorage: string = cupomOnStorage
      ? JSON.parse(cupomOnStorage)
      : '';

    const selectedDebtsFromStorage: IDebts[] | undefined =
      selectedDebtsOnStorage && selectedDebtsOnStorage !== 'undefined'
        ? JSON.parse(selectedDebtsOnStorage)
        : undefined;

    const installmentsFromStorage: InstallmentInfo[] = installmentsOnStorage
      ? JSON.parse(installmentsOnStorage)
      : [];

    const pixFromStorage: InstallmentInfo = pixOnStorage
      ? JSON.parse(pixOnStorage)
      : {};

    setCupomStatus(cupomStatusFromStorage ?? false);
    setCupom(cupomFromStorage);
    setInstallments(installmentsFromStorage);
    // setConsultCode(consultCodeFromStorage);
    setSelectedDebts(selectedDebtsFromStorage ?? []);
    setPix(pixFromStorage);

    if (pixFromStorage?.checked) {
      setIsPix(true);
    }
  }, []);

  // useEffect(() => {
  //   localStorage.setItem('@Zignet:cupom', JSON.stringify(cupom));
  //   localStorage.setItem('@Zignet:cupomStatus', JSON.stringify(cupomStatus));
  //   if (consultCode)
  //     localStorage.setItem('@Zignet:consultCode', JSON.stringify(consultCode));
  // }, [cupom, cupomStatus, consultCode]);

  useEffect(() => {
    if (selectedDebts && selectedDebts.length > 0) {
      localStorage.setItem(
        '@Zignet:selectedDebts',
        JSON.stringify(selectedDebts),
      );
    }
  }, [selectedDebts]);

  const handleSetCouponData = useCallback(
    (consult: IConsult) => {
      if (
        consult.campos.length > 0 &&
        consult.campos[0].conteudo === 'BBR5611'
      ) {
        consult.mensagemEntrada = `<p style="width: 100%;/* margin: 0 auto; */display: flex;align-items: center;justify-content: center;"><img src="https://blog.zignet.com.br/wp-content/uploads/2023/08/MG15.png"; width="320" border="0" alt="Cupom de desconto" /></p><span hidden>ZIGMG15</span>`;
      }

      if (consult.mensagemEntrada) {
        const groups = CUPOM_SPAN_REGEX.exec(consult.mensagemEntrada);
        if (groups) {
          setAvaliableCoupon(groups[2].trim());
        }
      }

      const parser = new DOMParser();
      const docEntry =
        consult?.mensagemEntrada &&
        parser.parseFromString(consult?.mensagemEntrada, 'text/html');
      const docExit =
        consult?.mensagemSaida &&
        parser.parseFromString(consult?.mensagemSaida, 'text/html');

      // let generalElement = null;
      // let partnerElement = null;
      let zignetFrotasElement = null;
      if (docEntry) {
        // generalElement =
        //   doc?.getElementById('general') || doc?.getElementById('GENERAL');
        // partnerElement =
        //   doc?.getElementById('partner') || doc?.getElementById('PARTNER');
        zignetFrotasElement =
          docEntry?.getElementById('frotas') ||
          docEntry?.getElementById('FROTAS');
      }

      if (zignetFrotasElement) {
        setEntryMessage(zignetFrotasElement.innerHTML);
      }
      // } else if (generalElement) {
      //   setEntryMessage(generalElement.innerHTML);
      // } else if (
      //   !generalElement &&
      //   !partnerElement &&
      //   !zignetFrotasElement &&
      //   consult?.mensagemEntrada
      // ) {
      //   setEntryMessage(consult?.mensagemEntrada);
      // }

      zignetFrotasElement = null;

      if (docExit) {
        // generalElement =
        //   doc?.getElementById('general') || doc?.getElementById('GENERAL');
        // partnerElement =
        //   doc?.getElementById('partner') || doc?.getElementById('PARTNER');
        zignetFrotasElement =
          docExit?.getElementById('frotas') ||
          docExit?.getElementById('FROTAS');
      }

      if (zignetFrotasElement) {
        setExitMessage(zignetFrotasElement.innerHTML);
      }
    },
    [CUPOM_SPAN_REGEX],
  );

  const handleGetConsult = useCallback(async () => {
    console.log('handleGetConsult ~ Processing selectedDebts ...');
    setInstallmentsLoading(true);
    try {
      if (!haveSameBatchId(selectedDebts)) {
        console.log(
          "handleGetConsult ~ selectedDebts doesn't have the same batchID.",
        );
        setInstallments([]);
        handleConsultClean(); // Clean Cache
        setInstallmentsLoading(false);
        return;
      }
      let debtsBatchCode: number = 0;

      // Usa reduce para combinar lógica de filtro, atualização de estado e transformação
      const debtsCodePrice: IDebtTotalPrice[] = selectedDebts.reduce<
        IDebtTotalPrice[]
      >((acc, debt) => {
        if (debt.batchId) {
          debtsBatchCode = debtsBatchCode || debt.batchId;
          acc.push({
            cod: parseInt(debt.consultNumber, 10),
            price: debt.price,
          });
        }
        return acc;
      }, []);

      if (!debtsBatchCode) {
        console.log(
          "handleGetConsult ~ selectedDebts doesn't have a valid batchID.",
        );
        setInstallments([]);
        handleConsultClean(); // Clean Cache
        setInstallmentsLoading(false);
        return;
      }

      // const mockCreateInstallments = {
      //   debtsBatchCode: 305,
      //   debtsCodePrice: JSON.parse(`[
      //     {
      //       "cod": 4995291,
      //       "price": 500.08
      //     },
      //     {
      //       "cod": 4995292,
      //       "price": 500.08
      //     },
      //     {
      //       "cod": 4995293,
      //       "price": 500.08
      //     },
      //     {
      //       "cod": 4995294,
      //       "price": 500.08
      //     }
      //   ]`),
      // };

      setBatchCode(debtsBatchCode);

      // // console.log("Handling Cupom...")
      // // handleSetCouponData(data); // handle Cupom

      console.log('handleGetConsult ~ Triggrering Get Installments...');
      await handleGetInstallments(debtsBatchCode, debtsCodePrice);
    } catch (error) {
    }
  }, [selectedDebts]);

  const validateBatch = useCallback(async () => {
    return (
      haveSameBatchId(selectedDebts) && areAllConsultDatesToday(selectedDebts)
    );
  }, [selectedDebts]);

  const handleGetInstallments = useCallback(
    async (batchCode: number, debtsPrice: IDebtTotalPrice[]) => {
      console.log('handleGetInstallments ~ Starting handleGetInstallments...');
      try {
        if (debtsPrice.length === 0) {
          setInstallments([]);
          setInstallmentsLoading(false);
          return;
        }

        console.log('handleGetInstallments ~ Requesting installments...');
        const installmentsResponse = await createBatchInstallments(batchCode, {
          consults: debtsPrice,
        });

        // FIXME - Coupon Related Code
        // console.log("handleGetInstallments ~ Handling cupom related processing...")
        // const cumpomStatusLSStr =
        //   localStorage.getItem('@Zignet:cupomStatus') ?? '';

        // const cupomStatusLocalStorage = JSON.parse(
        //   !cumpomStatusLSStr || cumpomStatusLSStr === 'undefined'
        //     ? 'false'
        //     : cumpomStatusLSStr,
        // );

        // if (cupomStatusLocalStorage) {
        //   setCupomStatus(cupomStatusLocalStorage);
        // }

        // setCupomStatus(response?.data?.data?.cupomAplicado);

        // if (cupom && !response?.data?.data?.cupomAplicado) {
        //   setCupom('');
        //   return;
        // }
        // console.log("handleGetInstallments ~ Cupom related processing finished")

        if (isCreateBatchInstallmentsResponse(installmentsResponse)) {
          handleResponseInstallmentsToInstallments(
            installmentsResponse.parcelas,
          );
        } else {
          setInstallmentsLoading(false);
        }
      } catch (error) {
        setInstallments([]);
        setInstallmentsLoading(false);
        return;
      }
    },
    [debtsTotal, cupom, selectedDebts],
  );

  const handleSelectInstallment = useCallback(
    // Function Related to `PaymentMethods` component
    (installment: InstallmentInfo) => {
      const newInstallments = installments.map((i) => ({
        ...i,
        checked: i.month === installment.month ? true : false,
      }));
      setInstallments(newInstallments);
      localStorage.setItem('@Zignet:parcelas', JSON.stringify(newInstallments));

      if (!_.isEmpty(pix)) {
        const newPix = {
          ...pix,
          checked: false,
        };
        setPix(newPix);
        localStorage.setItem('@Zignet:pix', JSON.stringify(newPix));
      }
      isPix && setIsPix(false);
    },
    [installments, pix, isPix],
  );

  const handleSelectPix = useCallback(() => {
    //Function Related to `PaymentMethods` component
    const newPix = {
      ...pix,
      checked: true,
    };
    setPix(newPix as InstallmentInfo);
    localStorage.setItem('@Zignet:pix', JSON.stringify(newPix));

    const newInstallments = installments.map((i) => ({
      ...i,
      checked: false,
    }));
    setInstallments(newInstallments);
    localStorage.setItem('@Zignet:parcelas', JSON.stringify(newInstallments));
    !isPix && setIsPix(true);
  }, [installments, pix, isPix]);

  const handleSetCupom = useCallback((cupom: string) => {
    setCupom(cupom);
  }, []);

  const handleConsultClean = useCallback((keepDebts?: boolean) => {
    localStorage.removeItem('@Zignet:cupomStatus');
    localStorage.removeItem('@Zignet:cupom');
    localStorage.removeItem('@Zignet:consultCode');
    if (!keepDebts) {
      localStorage.removeItem('@Zignet:selectedDebts');
      localStorage.removeItem('@Zignet:debtsForTable');
      localStorage.removeItem('@Zignet:allDebtsSelected');
    }
    localStorage.removeItem('@Zignet:parcelas');
    localStorage.removeItem('@Zignet:pix');
    setIsPix(false);
    // setConsult(undefined);
    // setConsultCode(undefined);
  }, []);

  const handleRefetchDebts = useCallback(
    async (
      consultsPackageStatus: IGetBatchConsultStatusResponse | undefined,
    ) => {
      if (!consultsPackageStatus || !consultsPackageStatus.consultas) {
        return;
      }

      try {
        const refetchedDebts: IDebts[] = await getBatchDebtsByIdentifications(
          consultsPackageStatus.consultas,
        );
        const filteredDebts = filterBySelectedDebts({
          refetchedDebts,
          selectedDebts,
        });
        setSelectedDebts(filteredDebts);
      } catch (error: any) {
        if (error?.response?.status === 404) {
          console.warn(
            'Nenhum débito encontrada.',
          );
          setSelectedDebts([]);
        } else {
          console.error('Erro ao buscar débito:', error);
        }
      }
    },
    [selectedDebts],
  );

  return (
    <BatchPaymentContext.Provider
      value={{
        installmentsLoading,
        // consultLoading,
        handleSetCupom,
        cupomStatus,
        entryMessage,
        exitMessage,
        cupom,
        availableCoupon,
        setSelectedDebts,
        selectedDebts,
        debtsForTable,
        handleGetConsult,
        validateBatch,
        handleSelectInstallment,
        handleSelectPix,
        installments,
        pix,
        isPix,
        batchCode,
        ipvaTotal,
        licenciamentoTotal,
        dpvatTotal,
        multasTotal,
        outrosTotal,
        debtsTotal,
        allDebtsSelected,
        // consultCode,
        handleConsultClean,
        handleRefetchDebts,
        // consult,
      }}
    >
      {children}
    </BatchPaymentContext.Provider>
  );
};

export function useBatchPayment(): BatchPaymentContextData {
  const context = useContext(BatchPaymentContext);

  if (!context) {
    throw new Error('useBatchPayment must be used within an PaymentProvider');
  }

  return context;
}
